/*
 * File: index.ts
 * File Created: 2023-12-05 02:19:57
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-10-19 07:54:44
 * Modified By: Val Liu
 * -----
 */

import { BehaviorSubject, Observable, Subscription } from "rxjs";

import { ILang, IBaseLang } from "./core";
export * from "./core";

import Config from "@/config/default/i18n";
import Vue from "vue";

export type IAllLang = ILang & IBaseLang;

let languageMap: { [index: string]: [string, ILang] } = {};
export const defaultLanguage: string = Config.defaultLang || "en-US";

export function RegisterLanguage(name: string, description: string) {
    return (Class: any) => {
        languageMap[name] = [description, new Class()];
    };
}

let languageBaseMap: { [index: string]: [string, IBaseLang] } = {};
export function RegisterBaseLanguage(name: string, description: string) {
    return (Class: any) => {
        languageBaseMap[name] = [description, new Class()];
    };
}

export class Language {
    /// get all languages
    list(): { [index: string]: string } {
        return Object.keys(languageMap).reduce((final, key) => {
            final[key] = languageMap[key][0];
            return final;
        }, {} as any);
    }

    /// test the translation key exists.
    keyExists(key: any): boolean {
        return this.getLanguageBaseObject()[key] != null;
    }

    translate<T extends keyof IAllLang>(
        key: T,
        data?: { [index: string]: any },
    ): IAllLang[T];
    translate<T extends string>(key: T, data?: { [index: string]: any }): T;
    translate<T extends string>(key: T, data?: any[]): T;
    translate<T extends string>(key: T, ...args: any[]): T;
    translate(key: unknown, ...args: any[]): string {
        if (typeof key != "string") return key as any;
        const regex_trans = /^[a-zA-Z]\_/;
        // 參數正規化：
        // - 沒帶第二參數 → data = undefined
        // - 單一參數且是 object/array → data = 該物件/陣列
        // - 多參數 → data = args（位置佔位用 {0}{1}...）
        let data: any = undefined;
        if (args.length === 0) {
            data = undefined;
        } else if (args.length === 1) {
            const a0 = args[0];
            if (typeof a0 === "string") {
                data = [a0];                     // ← 關鍵：避免被逐字切
            } else if (Array.isArray(a0) || (a0 && typeof a0 === "object")) {
                data = a0;                       // 具名或位置資料
            } else {
                // 支援 number/boolean 等純量
                data = [a0];
            }
        } else {
            data = args;                        // 多參數 → 位置參數陣列
        }

        let vkey: any = key;
        let result: string = "";

        /// check if it's an real translate key
        if (regex_trans.test(vkey)) {
            /// perform translate
            result =
                this.getLanguageObject()[vkey] ??
                this.getLanguageBaseObject()[vkey] ??
                `(!) ${
                    this.getLanguageObject(defaultLanguage)[vkey] ??
                    this.getLanguageBaseObject(defaultLanguage)[vkey] ??
                    vkey
                }`;
        } else {
            result = key;
        }

        // 多輪自內向外處理占位（避免一次就吃到外層）
        const TOKEN_INNER = /\{([^{}]+)\}/g;   // 僅配對「內部不含再一層 {}」的 token
        let guard = 0;                         // 安全護欄，避免無限循環
        while (/\{[^{}]+\}/.test(result) && guard++ < 50) {
            result = result.replace(TOKEN_INNER, (_m, inner: string) => {
                const token = inner.trim();

                // 1) inline call：name(args)
                const call = /^([0-9A-Za-z_-]+)\s*\((.*)\)\s*$/.exec(token);
                if (call) {
                    const name = call[1];
                    const argStr = call[2].trim();
                    try {
                        const argsForCall = parseInlineArgs(argStr);
                        return this.translate(name, ...argsForCall);
                    } catch {
                        return _m; // 解析失敗 → 保留原樣
                    }
                }

                // 2) 舊規則：data 優先，否則當翻譯鍵，否則原樣
                if (data && Object.prototype.hasOwnProperty.call(data, token)) {
                    return this.translate(data[token]);
                }
                if (/^[a-zA-Z]\_/.test(token)) {
                    return this.translate(token);
                }
                return _m;
            });
        }
        return result;
    }

    /// getter / getter of current language
    set(name: string) {
        if (!name) return;
        if (Object.keys(languageMap).indexOf(name) >= 0) this.currentLanguage.next(name);
        else throw `Language <${name}> not exists.`;
    }
    get(): string {
        return this.currentLanguage.getValue();
    }

    getObservable(): Observable<string> {
        return this.currentLanguage.asObservable();
    }

    /// private helpers
    private currentLanguage: BehaviorSubject<string> = new BehaviorSubject<string>(
        defaultLanguage,
    );
    /// get language object with lang name (ex: zh-TW)
    private getLanguageObject(lang?: string): ILang {
        return !lang
            ? languageMap[this.currentLanguage.getValue()][1]
            : languageMap[lang][1];
    }
    /// get base language object with lang name (ex: zh-TW)
    private getLanguageBaseObject(lang?: string): IBaseLang {
        return !lang
            ? languageBaseMap[this.currentLanguage.getValue()][1]
            : languageBaseMap[lang][1];
    }
}

const lang = new Language();
export default lang;
export { lang };

declare module "vue/types/vue" {
    export interface Vue {
        $_<K extends keyof IAllLang | (string & {}) | null | undefined>(
            key: K,
            ...args: [] | [Record<string, any> | any[]] | any[]
        ): K extends keyof IAllLang ? IAllLang[K] : string;

        $lang: Language;
    }
}

const sjLang: BehaviorSubject<Language> = new BehaviorSubject<Language>(lang);

export const LangPlugin = {
    install: function (Vue: any) {
        if (Vue._val_lang_installed) return;
        Vue._val_lang_installed = true;

        Vue.mixin({
            /// 2024/10/19 Val: no need this to update language
            /// and also cause bug on: v-strict-number
            // created() {
            //     let subscription = lang.getObservable().subscribe((name) => {
            //         this.$forceUpdate();
            //     });
            //     this.$once("hook:beforeDestroy", () => subscription.unsubscribe());
            // },

            subscriptions: () => {
                return {
                    $lang: sjLang,
                };
            },

            methods: {
                $_: LangCache(lang.translate.bind(lang)),
            },
        });
    },
};

/// caching the translate data, prevent re-generate.
const cachedLang: any = {};
function LangCache<T extends Function>(wrapped: T): T {
    return function () {
        let curLang = lang.get();
        let [key, data] = arguments;
        let dataString = data ? JSON.stringify(data) : "";
        let cached =
            (!cachedLang[curLang] && (cachedLang[curLang] = {}), cachedLang[curLang]);
        cached = (!cached[key] && (cached[key] = {}), cached[key]);
        cached = cached[dataString];
        if (cached) return cached;

        let value = wrapped(...arguments);
        cachedLang[curLang][key][dataString] = value;
        return value;
    } as any as T;
}

// ---------------- helpers ----------------

// 解析 (...) 內的參數為陣列：
// 支援三種：
//   1) "小明","台北"     → ["小明","台北"]（位置參數）
//   2) ["小明","台北"]   → [ ["小明","台北"] ]（單一陣列 data）
//   3) {"user":"小明"}   → [ {user:"小明"} ]（單一物件 data）
function parseInlineArgs(s: string): any[] {
    if (!s) return [];
    const first = s[0];
    if (first === "{" || first === "[") {
        const swapped = s
            .replace(/[‘’]/g, "'").replace(/[“”]/g, '"')
            .replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, (_m, s) => `"${s.replace(/"/g, '\\"')}"`);
        const parsed = JSON.parse(swapped);
        return [parsed]; // 單一 data 引數（物件/陣列）
    }
    // 逗號分隔的引號字串清單 -> 多個位置參數
    return splitQuotedArgs(s).map(x => x.value);
}

// 僅支援被引號包住的字串（' 或 "），可用 \ 跳脫引號與逗號
function splitQuotedArgs(s: string): { value: string }[] {
    const out: { value: string }[] = [];
    let i = 0;
    while (i < s.length) {
    while (i < s.length && (s[i] === "," || /\s/.test(s[i]))) i++;
    if (i >= s.length) break;
    const ch = s[i];
    if (ch === "'" || ch === '"') {
        const quote = ch; i++;
        let buf = "";
        while (i < s.length) {
        const c = s[i++];
        if (c === "\\") { if (i < s.length) buf += s[i++]; continue; }
        if (c === quote) break;
        buf += c;
        }
        out.push({ value: buf });
    } else {
        // 非引號起頭：讀到下一個逗號為止（保留原字面）
        let start = i;
        while (i < s.length && s[i] !== ",") i++;
        out.push({ value: s.slice(start, i).trim() });
    }
    }
    return out;
}